import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDownIcon, ChevronRightIcon, EllipsisHorizontalIcon, PlusIcon } from '@heroicons/react/24/outline';
import { DataTable, DataTableBody, DataTableCell, DataTableHead, DataTableRow, TableHeader } from '../ui/DataTable';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { AddModelDialog } from '../modelcatalog/AddModelDialog';
import { ModelFallbackNote } from '../modelcatalog/ModelFallbackNote';
import { formatCheckedAgo, formatDay } from '../modelcatalog/review/reviewUtils';
import { useOptionalToast } from '../../contexts/ToastContext';
import { useUpdateModel } from '../../hooks/useModels';
import { refusalReason } from '../../utils/apiRefusal';
import { formatPricePerMTok, formatTokenCount } from '../../utils/formatUtils';
import { modelLabel } from '../modelconfigs/modelConfigRules';
import type { Model, ModelProvider, ModelReviewField, ModelStatus } from '../../types';
import { ModelConfigDialog } from './ModelConfigDialog';
import { ModelConfigsInline } from './ModelConfigsInline';

/**
 * The selected provider's Models (task 1167): the old Models tab's columns
 * minus Provider (the rail answers it) plus a Configs count. A value the
 * review found out of date carries a "differs" mark linking to the Review view
 * for that Model. Retired Models are listed for staff (pickers keep hiding
 * them). Past `FOLD_AFTER` rows, older Models fold behind a "show N older" row.
 *
 * Expanding a Model shows its pricing tiers and the Model Configs that run on
 * it (`ModelConfigsInline`). Every row's menu offers, in order: Add config on
 * this Model, Edit model, Open in Review, Deprecate — whether or not the row is
 * expanded.
 */

/** Rows shown before older Models fold away. */
export const FOLD_AFTER = 5;

const STATUS_BADGE: Record<ModelStatus, string> = {
  active: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
  deprecated: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
  retired: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
};

const MENU_ITEM_CLASSES =
  'flex w-full items-center px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 data-[focus]:bg-gray-50 dark:data-[focus]:bg-gray-700 data-[disabled]:opacity-50';

const ADD_BUTTON_CLASSES =
  'inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500';

/** Newest first; Models with no release date after dated ones, then by name. */
const byRecency = (a: Model, b: Model) =>
  Date.parse(b.releasedAt ?? '0') - Date.parse(a.releasedAt ?? '0') || modelLabel(a).localeCompare(modelLabel(b));

interface ProviderModelsTableProps {
  provider: ModelProvider;
  providers: ModelProvider[];
  models: Model[];
  /** The whole catalog, so a successor on another provider still has a name. */
  catalog: Model[];
  configCounts: Map<string, number>;
  differs: Map<string, ModelReviewField[]>;
  canManage: boolean;
  canCreateConfig: boolean;
  /** Review view for one Model, or null when Review is not available. */
  reviewPathFor: ((model: Model) => string) | null;
  /** A Model to open expanded (the `?model=` deep link and the retired config pages' redirects). */
  expandModelId?: string | null;
  /** Open the Add config dialog on `expandModelId` once (the retired New Model Config route). */
  addConfigOnOpen?: boolean;
}

function Differs({ model, fields, field, reviewPathFor }: {
  model: Model;
  fields: ModelReviewField[] | undefined;
  field: ModelReviewField | ModelReviewField[];
  reviewPathFor: ((model: Model) => string) | null;
}) {
  const wanted = Array.isArray(field) ? field : [field];
  if (!fields || !wanted.some((f) => fields.includes(f)) || !reviewPathFor) return null;
  return (
    <Link
      to={reviewPathFor(model)}
      data-testid={`model-differs-${model.modelId}-${wanted[0]}`}
      title="The review found the provider's value differs. Open it in Review."
      className="ml-1 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 hover:underline dark:bg-amber-900/40 dark:text-amber-200"
    >
      differs
    </Link>
  );
}

export function ProviderModelsTable({
  provider,
  providers,
  models,
  catalog,
  configCounts,
  differs,
  canManage,
  canCreateConfig,
  reviewPathFor,
  expandModelId,
  addConfigOnOpen = false,
}: ProviderModelsTableProps) {
  const navigate = useNavigate();
  const toast = useOptionalToast();
  const updateModel = useUpdateModel();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(expandModelId ? [expandModelId] : []));
  const [showAll, setShowAll] = useState(false);
  const [addingModel, setAddingModel] = useState(false);
  const [configFor, setConfigFor] = useState<Model | null>(null);
  const [deprecating, setDeprecating] = useState<Model | null>(null);

  const rows = useMemo(() => [...models].sort(byRecency), [models]);

  // A deep-linked Model opens expanded, and unfolds the list if it was folded.
  useEffect(() => {
    if (!expandModelId) return;
    setExpanded((prev) => new Set(prev).add(expandModelId));
    const index = rows.findIndex((m) => m.id === expandModelId);
    if (index >= FOLD_AFTER) setShowAll(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandModelId, rows.length]);

  // The retired New Model Config route lands here with the Add config dialog open.
  const [addConfigDone, setAddConfigDone] = useState(false);
  useEffect(() => {
    if (!addConfigOnOpen || addConfigDone || !expandModelId || !canCreateConfig) return;
    const target = rows.find((m) => m.id === expandModelId);
    if (target && target.status !== 'retired') setConfigFor(target);
    if (target) setAddConfigDone(true);
  }, [addConfigOnOpen, addConfigDone, expandModelId, canCreateConfig, rows]);

  const folds = rows.length > FOLD_AFTER + 1 && !showAll;
  const visible = folds ? rows.slice(0, FOLD_AFTER) : rows;
  const hidden = folds ? rows.slice(FOLD_AFTER) : [];

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const confirmDeprecate = async () => {
    if (!deprecating) return;
    const model = deprecating;
    try {
      await updateModel.mutateAsync({ id: model.id, data: { status: 'deprecated' } });
      toast?.success(`${model.modelId} marked deprecated`);
    } catch (error) {
      toast?.error(`Could not mark ${model.modelId} deprecated`, refusalReason(error));
    } finally {
      setDeprecating(null);
    }
  };

  return (
    <section data-testid={`models-table-${provider.key}`} className="mt-6">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Models</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The provider&apos;s facts about each Model. Expand a Model to see the Model Configs that run on it; agents
            pick a config, not a Model. Retired Models are shown to staff and hidden from pickers.
          </p>
        </div>
        {canManage && (
          <button type="button" onClick={() => setAddingModel(true)} className={ADD_BUTTON_CLASSES}>
            <PlusIcon className="h-4 w-4" />
            Add model
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="flex h-24 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
          No Models for {provider.displayName} in the catalog.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <DataTable>
            <DataTableHead>
              <TableHeader>Model</TableHeader>
              <TableHeader>Context</TableHeader>
              <TableHeader>Max Output</TableHeader>
              <TableHeader>$/MTok in · out</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Last checked</TableHeader>
              <TableHeader>Configs</TableHeader>
              <TableHeader>
                <span className="sr-only">Actions</span>
              </TableHeader>
            </DataTableHead>
            <DataTableBody>
              {visible.map((model) => {
                const label = modelLabel(model);
                const isExpanded = expanded.has(model.id);
                const fields = differs.get(model.id);
                const tiers = Array.isArray(model.pricingTiers) ? model.pricingTiers : [];
                const configs = configCounts.get(model.id) ?? 0;
                return (
                  <Fragment key={model.id}>
                    <DataTableRow>
                      <DataTableCell>
                        <div className="flex items-start gap-2" data-testid={`models-row-${model.modelId}`}>
                          <button
                            type="button"
                            onClick={() => toggle(model.id)}
                            aria-expanded={isExpanded}
                            aria-label={`${isExpanded ? 'Hide' : 'Show'} configs for ${label}`}
                            data-testid={`models-row-toggle-${model.modelId}`}
                            className="mt-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            {isExpanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                          </button>
                          <div className="min-w-0">
                            <Link
                              to={`/model-catalog/models/${model.id}`}
                              className="text-sm font-medium text-gray-900 hover:text-indigo-600 hover:underline dark:text-white dark:hover:text-indigo-400"
                            >
                              {label}
                            </Link>
                            {model.displayName && (
                              <div className="font-mono text-xs text-gray-500 dark:text-gray-400">{model.modelId}</div>
                            )}
                            {model.supportsTemperature === false && (
                              <span
                                data-testid={`model-no-temperature-${model.modelId}`}
                                title="This model does not accept a temperature; calls leave it out."
                                className="mt-1 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                              >
                                No temperature
                              </span>
                            )}
                          </div>
                        </div>
                      </DataTableCell>
                      <DataTableCell className="text-sm text-gray-600 dark:text-gray-300">
                        <span title={`${model.contextWindow.toLocaleString('en-US')} tokens`}>{formatTokenCount(model.contextWindow)}</span>
                        {tiers.length > 0 && <span className="text-xs text-gray-400"> · tiered</span>}
                        <Differs model={model} fields={fields} field="contextWindow" reviewPathFor={reviewPathFor} />
                      </DataTableCell>
                      <DataTableCell className="text-sm text-gray-600 dark:text-gray-300">
                        {model.maxOutputTokens.toLocaleString('en-US')}
                        <Differs model={model} fields={fields} field="maxOutputTokens" reviewPathFor={reviewPathFor} />
                      </DataTableCell>
                      <DataTableCell className="text-sm text-gray-600 dark:text-gray-300">
                        {formatPricePerMTok(model.inputPriceMTok)} · {formatPricePerMTok(model.outputPriceMTok)}
                        <Differs
                          model={model}
                          fields={fields}
                          field={['inputPriceMTok', 'outputPriceMTok', 'cacheReadPriceMTok', 'cacheWritePriceMTok', 'pricingTiers']}
                          reviewPathFor={reviewPathFor}
                        />
                      </DataTableCell>
                      <DataTableCell className="text-sm text-gray-600 dark:text-gray-300">
                        <span
                          data-testid={`model-status-${model.status}`}
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[model.status] ?? STATUS_BADGE.retired}`}
                        >
                          {model.status}
                        </span>
                        {model.shutdownDate && <div className="mt-0.5 text-xs">shuts down {formatDay(model.shutdownDate)}</div>}
                        <ModelFallbackNote model={model} catalog={catalog} />
                      </DataTableCell>
                      <DataTableCell className="text-sm text-gray-600 dark:text-gray-300">
                        {model.lastSyncedAt ? (
                          <time dateTime={model.lastSyncedAt} title={new Date(model.lastSyncedAt).toLocaleString()}>
                            {formatCheckedAgo(model.lastSyncedAt)}
                          </time>
                        ) : (
                          'never'
                        )}
                      </DataTableCell>
                      <DataTableCell className="text-sm text-gray-600 dark:text-gray-300">
                        <span data-testid={`models-row-configs-${model.modelId}`}>{configs}</span>
                      </DataTableCell>
                      <DataTableCell className="w-12 whitespace-nowrap text-right">
                        <Menu as="div" className="relative inline-block text-left">
                          <MenuButton
                            data-testid={`models-row-menu-${model.modelId}`}
                            aria-label={`Actions for ${label}`}
                            className="inline-flex items-center rounded-md p-1 text-gray-400 hover:text-gray-600 focus:outline-none dark:hover:text-gray-200"
                          >
                            <EllipsisHorizontalIcon className="h-5 w-5" />
                          </MenuButton>
                          <MenuItems className="absolute right-0 z-50 mt-2 w-52 origin-top-right rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-800">
                            <MenuItem disabled={!canCreateConfig || model.status === 'retired'}>
                              <button type="button" onClick={() => setConfigFor(model)} className={MENU_ITEM_CLASSES}>
                                Add config on this Model
                              </button>
                            </MenuItem>
                            <MenuItem disabled={!canManage}>
                              <button
                                type="button"
                                onClick={() => navigate(`/model-catalog/models/${model.id}?edit=1`)}
                                className={MENU_ITEM_CLASSES}
                              >
                                Edit model
                              </button>
                            </MenuItem>
                            <MenuItem disabled={!reviewPathFor}>
                              <button
                                type="button"
                                onClick={() => reviewPathFor && navigate(reviewPathFor(model))}
                                className={MENU_ITEM_CLASSES}
                              >
                                Open in Review
                              </button>
                            </MenuItem>
                            <MenuItem disabled={!canManage || model.status !== 'active'}>
                              <button type="button" onClick={() => setDeprecating(model)} className={MENU_ITEM_CLASSES}>
                                Deprecate…
                              </button>
                            </MenuItem>
                          </MenuItems>
                        </Menu>
                      </DataTableCell>
                    </DataTableRow>
                    {isExpanded && (
                      <DataTableRow>
                        <DataTableCell colSpan={8} className="bg-gray-50 dark:bg-gray-900/40">
                          {tiers.length > 0 && (
                            <div className="mb-3 pl-6 text-xs text-gray-600 dark:text-gray-300">
                              <div className="mb-1 font-medium text-gray-700 dark:text-gray-200">Pricing tiers</div>
                              <ul className="space-y-0.5">
                                {tiers.map((tier) => (
                                  <li key={tier.over}>
                                    Over {formatTokenCount(tier.over)} input tokens: {formatPricePerMTok(tier.inputPriceMTok)} in ·{' '}
                                    {formatPricePerMTok(tier.outputPriceMTok)} out
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <ModelConfigsInline model={model} />
                        </DataTableCell>
                      </DataTableRow>
                    )}
                  </Fragment>
                );
              })}
              {hidden.length > 0 && (
                <DataTableRow>
                  <DataTableCell colSpan={8}>
                    <button
                      type="button"
                      onClick={() => setShowAll(true)}
                      data-testid={`models-show-older-${provider.key}`}
                      className="text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      Show {hidden.length} older {provider.displayName} Model{hidden.length === 1 ? '' : 's'}
                    </button>
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      ({hidden.map(modelLabel).join(', ')})
                    </span>
                  </DataTableCell>
                </DataTableRow>
              )}
            </DataTableBody>
          </DataTable>
        </div>
      )}

      {canManage && <AddModelDialog open={addingModel} onClose={() => setAddingModel(false)} providers={providers} />}
      <ModelConfigDialog open={configFor !== null} onClose={() => setConfigFor(null)} model={configFor} />
      <ConfirmDialog
        isOpen={deprecating !== null}
        onClose={() => setDeprecating(null)}
        onConfirm={confirmDeprecate}
        title={`Deprecate ${deprecating ? modelLabel(deprecating) : 'this Model'}?`}
        message="Deprecated Models stay usable by their configs and are marked in every picker. Set a shutdown date and a successor on the Model page."
        confirmLabel="Deprecate"
        isLoading={updateModel.isPending}
      />
    </section>
  );
}

export default ProviderModelsTable;
